import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { iif, map, Observable, of, Subscription, switchMap, take, tap, zip } from 'rxjs';
import { Router } from '@angular/router';
import isEqual from 'lodash-es/isEqual';

import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchType } from '@search-bar/entities/search-type';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { isNullOrUndefined } from '@app/util/util';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { EventProperties } from '@avo/interface/trigger-props';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { activitiesHomeRootRoute } from '@activities/constants/activities.routes';
import { productResultsRoute } from '@app/constants/app-routing.constants';
import { NavigationService } from '@app/services/navigation.service';
import { BRANCH_CODE, SALES_CHANNEL, SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { SearchBarBehavior } from '@shared/base-search-bar/entities/search-bar-behavior';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { AutoCompleteTravelExtra, InputTravelExtra } from '@shared/base-search-bar/entities/autocomplete-travel-extra';
import { generateResultsUrlParams } from '@activities/activities-results/mappers/activities-routing.mapper';

@Component({
  selector: 'app-activities-search-bar',
  templateUrl: './activities-search-bar.container.html',
  styleUrls: ['./activities-search-bar.container.sass'],
  standalone: false
})
export class ActivitiesSearchBarContainer implements OnInit, OnDestroy {

  @Input() source: EVENT_SOURCE | string;
  @Input() searchBarBehavior = SearchBarBehavior.OnHomePage;

  public readonly icons = Icon;

  public readonly departureType = PlaceType.DEPARTURE;
  public readonly swipeStyle = SwipeType.FULL;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  public readonly iconColor = IconFillColor;

  public readonly searchType = SearchType.TOUR_AND_ACTIVITIES;

  public isSubmitted = false;
  public searching = false;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly facade: BaseSearchBarFacade,
    private readonly avoService: AvoService,
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly amplitudeService: PropertiesAmplitudeService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) { }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.viewTravelSearchScreenEvent();
      this.subscriptions.add(
        this.viewTravelSearchScreenEventListener$()
      );
      if (
        this.searchBarBehavior === SearchBarBehavior.OnHomePage || this.searchBarBehavior === SearchBarBehavior.OnLandingWidget
      ) {
        this.facade.loadSearchData(SearchBy.ACTIVITIES);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get editSearchIsActivated$(): Observable<boolean> {
    return this.facade.editSearchIsActivated$(HeaderProductType.Activities);
  }

  get input$(): Observable<InputTravelExtra> {
    return this.facade.inputTravelExtra$;
  }

  get isEmptyInput$(): Observable<boolean> {
    return this.input$.pipe(map(input => this.isEmptyInput(input, this.isSubmitted)));
  }

  get autoCompletePlaces$(): Observable<AutoCompleteTravelExtra> {
    return this.facade.autocompleteTravelExtra$;
  }

  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.facade.isLoadingAutocompletePlaces$;
  }

  get isSwipeUpSearchBar$(): Observable<boolean> {
    return this.isMobile$.pipe(map((isMobile) => isMobile && this.isEditable));
  }

  get buttonSize$(): Observable<ButtonSize> {
    return this.isMobile$.pipe(map((isMobile) => {
      return isMobile ? ButtonSize.RG : (this.resultsPage ? ButtonSize.SquareSM : ButtonSize.SquareRG);
    }))
  }

  get resultsPage(): boolean {
    return this.searchBarBehavior === SearchBarBehavior.OnResultsPage;
  }

  private get isEditable(): boolean {
    return this.resultsPage || this.searchBarBehavior === SearchBarBehavior.OnResultDetail ||
      this.searchBarBehavior === SearchBarBehavior.OnLandingWidget;
  }

  public toggleSearchBar(): void {
    zip(
      this.facade.isHeaderEditButtonVisible$, this.editSearchIsActivated$
    ).pipe(
      take(1),
      tap(([isEditButtonVisible, editSearchIsActivated]) => {
        this.setEditSearchByProduct(isEditButtonVisible, editSearchIsActivated);
      })
    ).subscribe().unsubscribe();
  }

  public goToResults(): void {
    zip(
      this.isMobile$, this.facade.searchData$, this.facade.isHeaderEditButtonVisible$, this.editSearchIsActivated$,
      this.amplitudeService.searchProperties$(SearchBy.ACTIVITIES).pipe(
        switchMap((searchProps) => this.avoService.getEventTriggerProps$(
          AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: this.source }, searchProps
        ))
      ),
    ).pipe(
      take(1),
      tap(([isMobile, searchData, isEditButtonVisible, editSearchIsActivated, trigger]) => {
        if (searchData?.inputTravelExtra?.Id && searchData?.inputTravelExtra?.codeType) {
          const searchCommands = [
            activitiesHomeRootRoute, productResultsRoute,
            generateResultsUrlParams(searchData?.inputTravelExtra)
          ];
          const urlCommands = this.navigationService.url?.split('/').filter(param => param);
          if (isMobile) {
            if (this.isEditable) {
              this.setEditSearchByProduct(isEditButtonVisible, editSearchIsActivated);
            } else {
              this.searching = true;
            }
          }
          if (!isEqual(searchCommands, urlCommands)) {
            this.AvoDispatch.selectTravelSearch({
              ...this.avoService.avoBaseProperties(trigger),
              source: this.avoService.getAvoSourceValueType(this.source),
            });
          }
          this.router.navigate(searchCommands, {
            queryParams: {
              [SOURCE_PARAM]: this.source,
              [BRANCH_CODE]: searchData?.branchCode,
              [SALES_CHANNEL]: searchData?.salesChannel
            }
          });
        }
      })
    ).subscribe().unsubscribe();
  }

  public placeInputChanged(input: ChangeInputPayload): void {
    if (input.isEmpty) {
      this.facade.resetAutocomplete();
    } else {
      this.facade.searchExtraByQuery(input.value);
    }
  }

  public clearAutocompletePlaces(): void {
    this.facade.resetAutocomplete();
  }

  public locationNotFoundEvent(props: EventProperties): void {
    this.avoService.getEventTriggerProps$()
      .pipe(
        take(1),
        tap(trigger => {
          this.AvoDispatch.travelLocationNotFound({
            ...this.avoService.avoBaseProperties(trigger),
            travelProductType: props.TRAVEL_PRODUCT_TYPE as string,
            controlId: props.CONTROL_ID as string,
            searchQuery: props.SEARCH_QUERY as string,
          })
        }
        )).subscribe().unsubscribe();
  }

  public popularSelectedEvent(props: EventProperties): void {
    this.avoService.getEventTriggerProps$(AMPLITUDE_EVENTS.SELECT_TRAVEL_POPULAR_DESTINATIONS, props)
      .pipe(
        take(1),
        tap(trigger => {
          triggerAmplitudeEventApp(trigger);
        })
      ).subscribe().unsubscribe();
  }

  public selectPlace(data: InputTravelExtra): void {
    this.facade.searchData$.pipe(
      take(1),
      tap((searchData) => {
        const search = { ...searchData };
        search.inputTravelExtra = data;
        this.facade.saveSearchData(search, SearchBy.ACTIVITIES);
        this.facade.selectTravelExtraInput(data);
      })
    ).subscribe().unsubscribe();
  }

  public navigateToBack(): void {
    this.navigationService.navigateToBack();
  }

  private isEmptyInput(place: InputTravelExtra, isSubmitted: boolean): boolean {
    return !!place && isNullOrUndefined(place.Id) && isSubmitted;
  }

  private setEditSearchByProduct(isEditButtonVisible: boolean, editSearchIsActivated: boolean): void {
    if (isEditButtonVisible) {
      const current = editSearchIsActivated ? HeaderProductType.Activities : null;
      this.facade.setEditSearchByProduct(current, HeaderProductType.Activities);
    }
  }

  private viewTravelSearchScreenEvent(): void {
    this.isSwipeUpSearchBar$.pipe(
      take(1),
      switchMap((isSwipeUpSearchBar) => iif(
        () => (
          (this.searchBarBehavior === SearchBarBehavior.OnHomePage) ||
          (this.searchBarBehavior === SearchBarBehavior.OnLandingWidget && !isSwipeUpSearchBar)
        ),
        this.registerViewTravelSearchScreen$(), of(null)
      ))
    ).subscribe().unsubscribe();
  }

  private viewTravelSearchScreenEventListener$(): Subscription {
    return this.editSearchIsActivated$.pipe(
      switchMap((editSearchIsActivated) => this.isSwipeUpSearchBar$.pipe(
        switchMap((isSwipeUpSearchBar) => iif(
          () => editSearchIsActivated && isSwipeUpSearchBar, this.registerViewTravelSearchScreen$(), of(null)
        ))
      ))
    ).subscribe();
  }

  private registerViewTravelSearchScreen$(): Observable<TriggerEvent> {
    return this.avoService.getEventTriggerProps$(
      AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, { SOURCE: this.source, TRAVEL_PRODUCT_TYPE: SearchBy.ACTIVITIES }
    ).pipe(
      take(1),
      tap(trigger => {
        this.AvoDispatch.viewTravelSearchScreen({
          ...this.avoService.avoBaseProperties(trigger),
          source: this.avoService.getAvoSourceValueType(this.source),
          travelProductType: trigger.EVENT_PROPS.TRAVEL_PRODUCT_TYPE as SearchBy
        });
      })
    );
  }

}
